import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import {
  ILocationSearchModel,
  usePaginatedLocationSearch,
} from '@user/query/useDuplicateLocationSearch'
import { useDuplicateCheck } from '@user/query/useDuplicateCheckNew'
import { useGetUserBranchListOnMount } from '@user/store/authorization/hooks/useGetUserBranchListOnMount'
import { ServiceLocation } from '@user/store/entityLocations'
import { locationSearchToShallowService } from './locationSearchToShallowService'
import { useCreateOppActions } from '@user/components/Location'
import { ServiceLocationsDrawer } from '@user/components/Location/SelectLocations/atoms'
import { useTheme } from '@mui/material/styles'
import Alert, { alertClasses } from '@mui/material/Alert'
import Button from '@mui/material/Button'
import AlertTitle from '@mui/material/AlertTitle'
import { useFormikContext } from 'formik'
import { ILeadFormModel } from '../../typings'
import { DuplicateDialog } from '@user/components/Leads/AddLeadPage/FormComponents/DuplicateAlert/DuplicateDialog/DuplicateDialog'
import { useSelector } from 'react-redux'
import {
  selectWorkWaveContactDuplicatesFilters,
  setPage as setWorkWaveContactPage,
} from '@user/store/duplicates/WorkwaveContacts/slice'
import {
  IUserPreferenceGeneralListItemType,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import {
  selectPestPacLocationDuplicatesFilters,
  setPage as setPestPacLocationPage,
} from '@user/store/duplicates/PestPacLocations/slice'
import { useAppDispatch } from '@user/store/hooks'
import { removeEmptyProps } from '@sales-center/common'

export type DuplicateAlertProps = {
  perPage?: number
  debounceDelay?: number
  createOppActions: ReturnType<typeof useCreateOppActions>
  handleDuplicateManageClick?: () => void
}

export function DuplicateAlertNew(props: DuplicateAlertProps) {
  const {
    debounceDelay = 1000,
    createOppActions,
    handleDuplicateManageClick,
  } = props
  const { onClickCreateOpp } = createOppActions
  const {
    perPage: rowsPerPage,
    page,
    orderBy,
    searchText,
  } = useSelector(selectWorkWaveContactDuplicatesFilters)

  const { perPage: rowsPerPagePestPac, page: pagePestPac } = useSelector(
    selectPestPacLocationDuplicatesFilters,
  )

  const dispatch = useAppDispatch()

  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const { values } = useFormikContext<ILeadFormModel>()

  //#region page visibility
  const [open, setOpen] = useState(false)
  const onOpen = useCallback(() => {
    setOpen(true)
  }, [])
  const onClose = useCallback(() => setOpen(false), [])
  //#endregion

  const pageSizeGeneralPreference = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.WorkWavePageSize,
  )

  const pestPacPageSizeGeneralPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type == IUserPreferenceGeneralListItemType.PestPacLocationPageSize,
  )

  const perPage = Number(pageSizeGeneralPreference?.value || rowsPerPage)

  const perPagePestPac = Number(
    pestPacPageSizeGeneralPreference?.value || rowsPerPagePestPac,
  )

  const createOppCallback = useCallback(
    (loc: ILocationSearchModel) => {
      const serviceLocation: ServiceLocation =
        locationSearchToShallowService(loc)
      onClickCreateOpp(serviceLocation)
    },
    [onClickCreateOpp],
  )

  const branchListLoadStatus = useGetUserBranchListOnMount()

  //#region internal results state
  const searchTermObj = useMemo(() => {
    const {
      firstName,
      lastName,
      company,
      emailAddresses,
      phoneNumber,
      address1,
    } = values

    const emailAddress =
      emailAddresses.find((x) => x.isPrimary)?.emailAddress ?? ''

    return {
      firstName,
      lastName,
      company,
      emailAddress,
      phone: phoneNumber,
      address: address1,
    }
  }, [values])

  const internalResponse = useDuplicateCheck(
    searchTermObj,
    perPage,
    debounceDelay,
    page,
    orderBy,
    searchText,
  )

  const internalCount = internalResponse.resultCount
  //#endregion

  //#region external results state
  const searchTermArr = useMemo(() => {
    const {
      firstName,
      lastName,
      company,
      emailAddresses,
      phoneNumber,
      address1,
    } = values

    const primaryEmail = emailAddresses.find((x) => x.isPrimary)?.emailAddress

    return [
      `${firstName} ${lastName}`.trim(),
      company,
      primaryEmail || '',
      phoneNumber,
      address1,
    ].filter((x) => x && x.length !== 0)
  }, [values])

  // When the search criteria entered on the form change, reset both grids back
  // to the first page. Otherwise a stale page from prior pagination makes the
  // backend skip past the (now smaller) result set, returning empty rows and a
  // count of 0 (the count is only requested on page 1).
  const internalSearchKey = useMemo(
    () => JSON.stringify(removeEmptyProps(searchTermObj)),
    [searchTermObj],
  )
  React.useEffect(() => {
    dispatch(setWorkWaveContactPage(1))
  }, [dispatch, internalSearchKey])

  const externalSearchKey = useMemo(
    () => searchTermArr.join('|'),
    [searchTermArr],
  )
  React.useEffect(() => {
    dispatch(setPestPacLocationPage(1))
  }, [dispatch, externalSearchKey])

  const externalResponse = usePaginatedLocationSearch(
    searchTermArr,
    perPagePestPac,
    debounceDelay,
    pagePestPac,
  )

  const externalCount = externalResponse.resultCount
  //#endregion

  const total = externalCount + internalCount

  const theme = useTheme()

  React.useEffect(() => {
    const handler = () => setOpen(true)
    window.addEventListener('open-duplicates-dialog', handler as EventListener)
    return () =>
      window.removeEventListener(
        'open-duplicates-dialog',
        handler as EventListener,
      )
  }, [])

  return (
    <>
      {total > 0 ? (
        <Alert
          color="warning"
          severity="warning"
          sx={{
            alignItems: 'center',
            width: '100%',
            backgroundColor: '#FDF0E6',
            [`& .${alertClasses.icon}`]: {
              color: '#F9BA1B',
            },
          }}
          action={
            <Button
              variant="text"
              size="small"
              onClick={onOpen}
              sx={{
                color: '#5F2B01',
                display: 'flex',
                padding: theme.spacing(0.5, 1.5),
                fontWeight: '500',
                marginRight: theme.spacing(0),
                textWrap: 'nowrap',
                textTransform: 'none',
                fontSize: '13px',
              }}
              data-test-id="check-duplicates"
            >
              Check Duplicates
            </Button>
          }
        >
          <AlertTitle
            sx={{
              color: '#5F2B01',
              lineHeight: '24px',
              margin: theme.spacing(0),
              fontWeight: '500',
              fontSize: '16px',
              letterSpacing: '0.15px',
            }}
          >{`Duplicate Alert (${total})`}</AlertTitle>
        </Alert>
      ) : null}

      <DuplicateDialog
        branchListLoadStatus={branchListLoadStatus}
        externalResponse={externalResponse}
        internalResponse={internalResponse}
        createOppCallback={createOppCallback}
        onClose={onClose}
        open={open}
        searchTermArr={searchTermArr}
        searchTermObj={searchTermObj}
        handleDuplicateManageClick={handleDuplicateManageClick}
      />

      <ServiceLocationsDrawer {...createOppActions} />
    </>
  )
}
